import React, { Component, Fragment } from 'react';

import classNames from 'classnames';
import Grid from 'components/grid/Grid';
import { Loader } from 'components/loader';
import { VALUES_PER_PAGE } from 'components/pagination/constants';
import { Table, TableRow, TableCell } from 'components/table';
import { ORDER_BY } from 'components/table/constants';
import { I18n } from 'components/translate';
import { convertToCommonArray } from 'helpers/Array';
import Compare from 'helpers/Compare';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';

import DataTableBody from './DataTableBody';
import DataTableContext from './DataTableContext';
import DataTableDataColumns from './DataTableDataColumns';
import DataTableFooter from './DataTableFooter';
import DataTableHeader from './DataTableHeader';
import DataTableHeaderCell from './DataTableHeaderCell';
import DataTablePagination from './DataTablePagination';
import DataTableSelectAll from './DataTableSelectAll';
import DataTableSelectItem from './DataTableSelectItem';

import './DataTable.scss';

class DataTable extends Component {
  // #region set up
  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.oneOf(Object.values(Compare.DATA_TYPES)),
        defaultOrderBy: PropTypes.oneOf(Object.values(ORDER_BY))
      })
    ).isRequired,
    fetchItems: PropTypes.func,
    itemKey: PropTypes.string,
    items: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.object),
      MobxPropTypes.observableArray
    ]).isRequired,
    perPage: PropTypes.number,
    loading: PropTypes.bool,
    onCellClick: PropTypes.func,
    onChangePage: PropTypes.func,
    onChangePerPage: PropTypes.func,
    onChangeSort: PropTypes.func,
    onRowClick: PropTypes.func,
    onSelect: PropTypes.func,
    pagination: PropTypes.object,
    selectAll: PropTypes.bool,
    selectedItems: PropTypes.array,
    emptyMessageLabel: PropTypes.string,
    emptyList: PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.string,
      checkOnlineEvent: PropTypes.bool
    })
  };

  static defaultProps = {
    itemKey: 'id',
    items: [],
    pagination: { page: 0 },
    perPage: VALUES_PER_PAGE.TEN,
    selectAll: void 0,
    selectedItems: [],
    emptyList: {
      label: 'users_manager.users_list.empty',
      checkOnlineEvent: false,
      icon: 'icon-empty'
    }
  };

  static Body = DataTableBody;
  static Cell = TableCell;
  static Footer = DataTableFooter;
  static Header = DataTableHeader;
  static HeaderCell = DataTableHeaderCell;
  static Pagination = DataTablePagination;
  static Row = TableRow;
  static SelectAll = DataTableSelectAll;
  static SelectItem = DataTableSelectItem;
  static DataColumns = DataTableDataColumns;

  constructor(props) {
    super(props);

    const sortOptions = {};

    props.columns
      .filter((c) => c.defaultOrderBy)
      .forEach((c) => (sortOptions[c.sortName] = c.defaultOrderBy));

    this.state = {
      items: props.items,
      loading: props.loading,
      pagination: props.pagination,
      perPage: props.perPage,
      selectAll: props.selectAll,
      selectedItems: props.selectedItems,
      sortOptions
    };

    this.onChangePage = this.onChangePage.bind(this);
    this.onChangePerPage = this.onChangePerPage.bind(this);
    this.onChangeSort = this.onChangeSort.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { itemKey, pagination, selectedItems, selectAll } = this.props;
    const update = {};

    const items = convertToCommonArray(this.props.items);
    const prevItems = convertToCommonArray(prevProps.items);

    if (!isEqual(items, prevItems)) {
      update.items = this.props.items;
    }

    if (!isEqual(pagination, prevProps.pagination)) {
      update.pagination = pagination;
    }

    if (!isEqual(selectedItems, prevProps.selectedItems)) {
      update.items = items.map((item) => {
        if (selectedItems.includes(item[itemKey])) {
          return { ...item, _checked: true };
        }

        return item;
      });
    }

    if (!isUndefined(selectAll) && selectAll !== prevProps.selectAll) {
      update.selectAll = selectAll;
    }

    return !isEmpty(update) ? this.setState(update) : null;
  }
  // #endregion

  // #region helpers
  get dataTableContext() {
    const { fetchItems, onCellClick, onRowClick } = this.props;

    return {
      actions: {
        fetchItems,
        onCellClick,
        onChangePage: this.onChangePage,
        onChangePerPage: this.onChangePerPage,
        onChangeSort: this.onChangeSort,
        onRowClick,
        onSelectAll: this.onSelect,
        onSelectItem: this.onSelect
      },
      data: this.data
    };
  }

  get data() {
    const { columns } = this.props;

    const { items, pagination, perPage, selectAll, sortOptions } = this.state;

    return {
      columns,
      items,
      pagination,
      perPage,
      selectAll,
      selectedItems: this.selectedItems,
      sortOptions
    };
  }

  get selectedItems() {
    const { items } = this.state;
    const { itemKey } = this.props;

    return items.filter((i) => i._checked).map((i) => i[itemKey]);
  }

  get isEmpty() {
    const { loading } = this.props;
    const { totalElements } = this.state.pagination;

    return !(loading || totalElements);
  }
  // #endregion

  // #region actions
  updateItems(page) {
    const { fetchItems } = this.props;
    const params = this.data;

    if (!isUndefined(page)) {
      Object.assign(params.pagination, { page });
    }

    fetchItems && fetchItems(params);
  }

  onChangePage(page) {
    const { onChangePage } = this.props;

    this.updateItems(page);
    onChangePage && onChangePage(page);
  }

  onChangePerPage(perPage) {
    const { onChangePerPage } = this.props;

    this.setState({ perPage }, () => {
      const resetPage = 0;

      this.updateItems(resetPage);

      onChangePerPage && onChangePerPage(perPage);
    });
  }

  onChangeSort(sortOption) {
    const { onChangeSort } = this.props;
    const { sortOptions } = this.state;

    const newSortOptions =
      (onChangeSort && onChangeSort(sortOption)) ||
      Object.assign({}, sortOptions, sortOption);

    this.setState({ sortOptions: newSortOptions }, () => {
      const resetPage = 0;

      this.updateItems(resetPage);
    });
  }

  onSelect(event, item) {
    const { checked } = event.target;
    const { items } = this.state;
    const { itemKey, onSelect } = this.props;
    let newItems = [];
    let selectAll = false;

    const isSelectAll = !item;

    if (isSelectAll) {
      selectAll = checked;
      newItems = items.map((item) => ({ ...item, _checked: selectAll }));
    } else {
      newItems = items.map((oldItem) =>
        oldItem[itemKey] === item[itemKey]
          ? { ...oldItem, _checked: checked }
          : oldItem
      );
    }

    this.setState({ items: newItems, selectAll });

    onSelect && onSelect({ checked, item, newItems, selectAll });
  }
  // #endregion

  // #region render
  render() {
    const { className, loading } = this.props;
    const classComponent = classNames('data-table', className);

    return (
      <DataTableContext.Provider value={this.dataTableContext}>
        <Table className={classComponent}>
          {this.isEmpty ? this.renderEmpty() : this.renderInner()}

          {loading && <Loader key="loader" />}
        </Table>
      </DataTableContext.Provider>
    );
  }

  renderEmpty() {
    const { emptyList } = this.props;

    return (
      <Grid block className="empty__history">
        <div className="empty__list__icon">
          <i className={`icon ${emptyList.icon}`} />
        </div>

        <div className="empty__history__description">
          <I18n
            label={emptyList.label}
            checkOnlineEvent={emptyList.checkOnlineEvent}
          />
        </div>
      </Grid>
    );
  }

  renderInner() {
    const { children } = this.props;

    return children ? children : this.renderDefault();
  }

  renderDefault() {
    return (
      <Fragment>
        <DataTableHeader />
        <DataTableBody />
        <DataTableFooter />
      </Fragment>
    );
  }
  // #endregion
}

export default DataTable;
